import { Suspense } from "react"
import { useController } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Lens } from "@hookform/lenses"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import type { AddressInput } from "@/modules/care-provider/simple-search/services/__generated__/useCareProviderQuickListQuery.graphql"
import { BetterDocCareProviderSelectionBlockedHint } from "@/modules/patients/detail/better-doc/components/better-doc-care-provider-selection-blocked-hint"
import type { ApplicableBetterDocUsage } from "@/modules/patients/detail/better-doc/hooks/useApplicableBetterDocUsage"
import { useClearSelectedCareProvider } from "@/modules/patients/detail/better-doc/hooks/useClearSelectedCareProvider"
import { canBetterDocUsageStillChange } from "@/modules/patients/detail/better-doc/lib/can-better-doc-usage-still-change"
import { isBetterDocRequired } from "@/modules/patients/detail/better-doc/lib/is-better-doc-required"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { type EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { CareProviderSearchType, TimeToTreatType } from "@/types/__generated__/union-types"

type CareProviderContentProps = {
  pointOfCareFormLens: Lens<PointOfCareFormType>
  hasCareProvider?: boolean
  hasMPNReferral?: boolean
  episodeOfCareProgressNoteData?: EpisodeOfCareProgressNoteData
  usePreset?: boolean
  useSpecialty?: boolean
  specialtyId?: string
  searchType: CareProviderSearchType
  showInsurerNetwork?: boolean
  patientAddress?: AddressInput | null
  timeToTreat?: TimeToTreatType
  allowUnknownCareProvider?: boolean
  applicableBetterDocUsage?: ApplicableBetterDocUsage
  onBetterDocCheck?: (
    callback: VoidFunction,
    event: React.FocusEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
  ) => void
  setCareProvider?: (careProvider: string | null) => void
}

const CARE_PROVIDER_ID_FIELD = "careProviderId"

const BETTER_DOC_REQUIRED_CARE_PROVIDER_MISSING_ERROR =
  "episodeOfCare.progressNote.pointOfCare.betterDocRequiredCareProviderMissing"

export const CareProviderContent = ({
  pointOfCareFormLens,
  hasCareProvider,
  hasMPNReferral,
  episodeOfCareProgressNoteData,
  specialtyId,
  searchType,
  showInsurerNetwork,
  patientAddress,
  timeToTreat,
  allowUnknownCareProvider,
  applicableBetterDocUsage,
  onBetterDocCheck,
  setCareProvider,
}: CareProviderContentProps) => {
  const { t } = useTranslation()

  const { field: careProviderIdField, fieldState: careProviderIdFieldState } = useController(
    pointOfCareFormLens.focus(CARE_PROVIDER_ID_FIELD).interop(),
  )
  const { field: betterDocSuggestionStatusField } = useController(
    pointOfCareFormLens.focus("betterDocSuggestionStatus").interop(),
  )

  const clearSelectedCareProvider = useClearSelectedCareProvider({ episodeOfCareProgressNoteData, setCareProvider })

  const missingConsultationDetailsThatCanChangeBetterDocUsage =
    applicableBetterDocUsage?.missingConsultationDetailsThatCanChangeBetterDocUsage ?? []
  const isBetterDocManualSearchNeeded = betterDocSuggestionStatusField.value === "MANUAL_SEARCH_NEEDED"
  const isCareProviderFieldEmpty = !careProviderIdField.value && !isBetterDocManualSearchNeeded
  const isCareProviderSelectionBlockedByBetterDoc =
    canBetterDocUsageStillChange(missingConsultationDetailsThatCanChangeBetterDocUsage) && isCareProviderFieldEmpty
  const isBetterDocRequestExpected =
    isBetterDocRequired(applicableBetterDocUsage?.betterDocUsage ?? null) &&
    isCareProviderFieldEmpty &&
    !episodeOfCareProgressNoteData?.betterDoc?.rejectionReason

  const getCareProviderError = (): string | undefined => {
    if (isCareProviderSelectionBlockedByBetterDoc || !careProviderIdFieldState.error) return undefined
    return isBetterDocRequestExpected
      ? BETTER_DOC_REQUIRED_CARE_PROVIDER_MISSING_ERROR
      : careProviderIdFieldState.error.message
  }

  const handleSelect = (value: string) => {
    careProviderIdField.onChange(value)
    setCareProvider?.(value)
  }

  return (
    <Suspense fallback={null}>
      <div className="w-full">
        <CareProviderSearch
          label={`${t("episodeOfCare.progressNote.pointOfCare.careProvider")}${hasCareProvider ? "*" : ""}`}
          name={CARE_PROVIDER_ID_FIELD}
          value={careProviderIdField.value}
          onSelect={handleSelect}
          onRemove={clearSelectedCareProvider}
          disabled={isCareProviderSelectionBlockedByBetterDoc}
          timeToTreat={timeToTreat}
          searchType={searchType}
          allowUnknownCareProvider={allowUnknownCareProvider}
          betterDocSuggestionStatus={betterDocSuggestionStatusField.value ?? undefined}
          onFocus={onBetterDocCheck}
          hasMPNReferral={hasMPNReferral}
          showInsurerNetwork={showInsurerNetwork}
          patientAddress={patientAddress}
          defaultAdvancedSearchFilters={{
            hasMPNReferral,
            specialtyId,
          }}
          error={getCareProviderError()}
          specialtyId={specialtyId}
          analyticsSectionName="point_of_care"
          episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        />
        {isCareProviderSelectionBlockedByBetterDoc && (
          <BetterDocCareProviderSelectionBlockedHint
            missingConsultationDetailsThatCanChangeBetterDocUsage={
              missingConsultationDetailsThatCanChangeBetterDocUsage
            }
            progressNoteId={episodeOfCareProgressNoteData?.id}
          />
        )}
      </div>
    </Suspense>
  )
}
